import { useCallback, useEffect, useMemo, useState } from 'react'
import type { InventoryItem, InventoryTransaction, OutstandingRow } from '@shared/types'
import { api, del, get, patch, post } from '../../lib/api'
import { PhotoPicker, type AttachedPhoto } from '../../components/PhotoPicker'
import { formatDateTime } from '../../lib/format'
import { useSession } from '../../state/session'
import { useToast } from '../../state/toast'
import {
  Button, EmptyState, ErrorNote, Field, NumberInput, Sheet, Spinner, TableScroll,
} from '../../components/ui'

type Tab = 'catalog' | 'out' | 'history'

export default function AdminInventory() {
  const { settings } = useSession()
  const toast = useToast()
  const [tab, setTab] = useState<Tab>('catalog')
  const [items, setItems] = useState<InventoryItem[] | null>(null)
  const [outstanding, setOutstanding] = useState<OutstandingRow[]>([])
  const [history, setHistory] = useState<InventoryTransaction[]>([])
  const [editing, setEditing] = useState<Partial<InventoryItem> | null>(null)
  const [search, setSearch] = useState('')

  const load = useCallback(async () => {
    const [itemsRes, outRes, txnRes] = await Promise.all([
      get<{ items: InventoryItem[] }>('/api/inventory/items?all=true'),
      get<{ outstanding: OutstandingRow[] }>('/api/inventory/outstanding?scope=all'),
      get<{ transactions: InventoryTransaction[] }>('/api/inventory/transactions?limit=100'),
    ])
    setItems(itemsRes.items)
    setOutstanding(outRes.outstanding)
    setHistory(txnRes.transactions)
  }, [])

  useEffect(() => {
    void load().catch(() => setItems([]))
  }, [load])

  const filtered = useMemo(() => {
    if (!items) return []
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter((item) =>
      item.name.toLowerCase().includes(q) || (item.sku ?? '').toLowerCase().includes(q))
  }, [items, search])

  if (!items) return <div className="py-16"><Spinner /></div>

  return (
    <div className="space-y-4">
      <div className="flex gap-1">
        {(['catalog', 'out', 'history'] as Tab[]).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setTab(value)}
            className={`btn flex-1 px-2 text-xs ${tab === value ? 'btn-primary' : 'btn-secondary'}`}
          >
            {value === 'catalog' ? 'Catalogue' : value === 'out' ? `Off site (${outstanding.length})` : 'History'}
          </button>
        ))}
      </div>

      {tab === 'catalog' && (
        <>
          <div className="flex gap-2">
            <input
              className="field flex-1"
              type="search"
              placeholder="Search gear"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button onClick={() => setEditing({ unit: 'ea', qtyTotal: 1, active: true })}>
              Add
            </Button>
          </div>

          <TableScroll>
            <table className="grid-table min-w-max">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>SKU</th>
                  <th>Category</th>
                  <th className="text-right">Owned</th>
                  <th className="text-right">Out</th>
                  <th className="text-right">On hand</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id} className={item.active ? '' : 'opacity-55'}>
                    <td className="font-semibold">
                      {item.photoThumbUrl && (
                        <img
                          src={item.photoThumbUrl}
                          alt=""
                          loading="lazy"
                          className="mr-2 inline-block size-7 rounded border border-line object-cover align-middle"
                        />
                      )}
                      {item.name}
                      {item.consumable && (
                        <span className="chip ml-2 text-graphite-2" style={{ boxShadow: 'inset 0 0 0 1px var(--wm-line-strong)' }}>
                          consumable
                        </span>
                      )}
                    </td>
                    <td className="font-mono text-xs text-graphite-2">{item.sku ?? '—'}</td>
                    <td className="text-graphite-2">{item.category ?? '—'}</td>
                    <td className="text-right font-mono tnum">{item.qtyTotal}</td>
                    <td className="text-right font-mono tnum">{item.qtyOut}</td>
                    <td
                      className={`text-right font-mono font-semibold tnum ${
                        item.qtyOnHand <= 0 ? 'text-alarm' : ''
                      }`}
                    >
                      {item.qtyOnHand}
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => setEditing(item)}
                        className="link text-xs"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        </>
      )}

      {tab === 'out' && (
        outstanding.length === 0 ? (
          <EmptyState title="Everything is back" body="Nothing is signed out of the shop right now." />
        ) : (
          <TableScroll>
            <table className="grid-table min-w-max">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>With</th>
                  <th>Site</th>
                  <th className="text-right">Qty</th>
                </tr>
              </thead>
              <tbody>
                {outstanding.map((row) => (
                  <tr key={`${row.itemId}-${row.userId}-${row.siteId ?? ''}`}>
                    <td className="font-semibold">{row.itemName}</td>
                    <td>{row.userName}</td>
                    <td className="text-graphite-2">{row.siteName ?? '—'}</td>
                    <td className="text-right font-mono tnum">
                      {row.qtyOut} {row.unit}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )
      )}

      {tab === 'history' && (
        <TableScroll>
          <table className="grid-table min-w-max">
            <thead>
              <tr>
                <th>When</th>
                <th>Direction</th>
                <th>Item</th>
                <th className="text-right">Qty</th>
                <th>Who</th>
                <th>Site</th>
                <th>Condition</th>
              </tr>
            </thead>
            <tbody>
              {history.map((txn) => (
                <tr key={txn.id}>
                  <td className="font-mono text-xs whitespace-nowrap tnum">
                    {formatDateTime(txn.occurredAt, settings.timezone)}
                  </td>
                  <td className={txn.direction === 'in' ? 'text-ok' : 'text-graphite'}>
                    {txn.direction === 'out' ? 'Out' : txn.direction === 'move' ? 'Moved' : 'In'}
                  </td>
                  <td className="font-semibold">{txn.itemName}</td>
                  <td className="text-right font-mono tnum">{txn.qty}</td>
                  <td>
                    {txn.userName}
                    {txn.fromHolderId && txn.fromHolderId !== txn.userId && (
                      <span className="text-xs text-graphite-2"> ({txn.fromHolderName}’s)</span>
                    )}
                  </td>
                  <td className="text-graphite-2">
                    {txn.direction === 'move' && txn.fromSiteName
                      ? `${txn.fromSiteName} → ${txn.siteName ?? '—'}`
                      : txn.siteName ?? '—'}
                  </td>
                  <td className="text-graphite-2">{txn.condition ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {history.length === 0 && (
            <EmptyState title="No movements" body="Gear coming and going shows up here." />
          )}
        </TableScroll>
      )}

      {editing && (
        <ItemSheet
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={async (message) => {
            setEditing(null)
            toast.success(message)
            await load()
          }}
        />
      )}
    </div>
  )
}

function ItemSheet({
  item, onClose, onSaved,
}: {
  item: Partial<InventoryItem>
  onClose: () => void
  onSaved: (message: string) => Promise<void>
}) {
  const { settings } = useSession()
  const [draft, setDraft] = useState<Partial<InventoryItem>>(item)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [photos, setPhotos] = useState<AttachedPhoto[]>([])
  const [photoBusy, setPhotoBusy] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const isNew = !item.id

  const uploadPhoto = async (itemId: string, photo: AttachedPhoto) => {
    const form = new FormData()
    form.set('photo_0', new File([photo.full], 'gear.jpg', { type: 'image/jpeg' }))
    form.set('thumb_0', new File([photo.thumb], 'gear-thumb.jpg', { type: 'image/jpeg' }))
    form.set('dims_0', `${photo.width}x${photo.height}`)
    const res = await api<{ item: InventoryItem }>(`/api/admin/inventory/items/${itemId}/photo`, {
      method: 'PUT', body: form,
    })
    return res.item
  }

  // An existing item's photo saves the moment it is taken; a new item's waits
  // for Save, since there is nothing to attach it to yet.
  const changePhoto = async (next: AttachedPhoto[]) => {
    setPhotos(next)
    if (isNew || !next[0]) return
    setPhotoBusy(true)
    setError(null)
    try {
      const updated = await uploadPhoto(item.id!, next[0])
      setDraft((d) => ({ ...d, photoUrl: updated.photoUrl, photoThumbUrl: updated.photoThumbUrl }))
      setPhotos([])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the photo')
    } finally {
      setPhotoBusy(false)
    }
  }

  const removePhoto = async () => {
    setPhotoBusy(true)
    try {
      await del(`/api/admin/inventory/items/${item.id}/photo`)
      setDraft((d) => ({ ...d, photoUrl: null, photoThumbUrl: null }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the photo')
    } finally {
      setPhotoBusy(false)
    }
  }

  const remove = async () => {
    setBusy(true)
    setError(null)
    try {
      await del(`/api/admin/inventory/items/${item.id}`)
      await onSaved(`${draft.name ?? 'Item'} deleted`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the item')
      setConfirmDelete(false)
      setBusy(false)
    }
  }

  const save = async () => {
    setBusy(true)
    setError(null)
    const payload = {
      name: draft.name,
      sku: draft.sku || null,
      category: draft.category,
      unit: draft.unit,
      qtyTotal: draft.qtyTotal,
      qtyOut: isNew ? undefined : draft.qtyOut,
      consumable: draft.consumable,
      active: draft.active,
    }
    try {
      if (isNew) {
        const res = await post<{ item: InventoryItem }>('/api/admin/inventory/items', payload)
        if (photos[0] && res.item) await uploadPhoto(res.item.id, photos[0])
      } else {
        await patch(`/api/admin/inventory/items/${item.id}`, payload)
      }
      await onSaved(isNew ? 'Item added' : 'Item updated')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the item')
      setBusy(false)
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title={isNew ? 'New item' : draft.name ?? 'Item'}
      footer={
        <Button variant="primary" size="lg" className="w-full" loading={busy} onClick={() => void save()}>
          Save item
        </Button>
      }
    >
      <div className="space-y-4">
        <div aria-busy={photoBusy}>
          <span className="label mb-1.5 block">Photo</span>
          {draft.photoUrl && photos.length === 0 ? (
            <div className="flex items-center gap-3">
              <img
                src={draft.photoThumbUrl ?? draft.photoUrl}
                alt={draft.name ?? 'Gear'}
                className="size-20 rounded border border-line object-cover"
              />
              <div className="flex flex-col gap-1">
                <PhotoPicker
                  photos={photos}
                  onChange={(next) => void changePhoto(next)}
                  max={1}
                  maxDimension={settings.photo.maxDimension}
                  quality={settings.photo.quality}
                />
                <Button className="min-h-9 px-3 text-xs" loading={photoBusy} onClick={() => void removePhoto()}>
                  Remove photo
                </Button>
              </div>
            </div>
          ) : (
            <PhotoPicker
              photos={photos}
              onChange={(next) => void changePhoto(next)}
              max={1}
              maxDimension={settings.photo.maxDimension}
              quality={settings.photo.quality}
            />
          )}
        </div>

        <Field label="Name" required>
          <input
            className="field"
            value={draft.name ?? ''}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="Rotary hammer drill"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="SKU">
            <input
              className="field font-mono"
              value={draft.sku ?? ''}
              onChange={(e) => setDraft({ ...draft, sku: e.target.value })}
              placeholder="DRL-HAM"
            />
          </Field>
          <Field label="Unit">
            <input
              className="field"
              value={draft.unit ?? 'ea'}
              onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
              placeholder="ea"
            />
          </Field>
        </div>

        <Field label="Category">
          <input
            className="field"
            value={draft.category ?? ''}
            onChange={(e) => setDraft({ ...draft, category: e.target.value })}
            placeholder="Power tool"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Owned" hint="Total the company has.">
            <NumberInput
              className="font-mono"
              min={0}
              value={draft.qtyTotal}
              onChange={(n) => setDraft({ ...draft, qtyTotal: n ?? 0 })}
            />
          </Field>
          {!isNew && (
            <Field label="Currently out" hint="Correct after a physical count.">
              <NumberInput
                className="font-mono"
                min={0}
                value={draft.qtyOut}
                onChange={(n) => setDraft({ ...draft, qtyOut: n ?? 0 })}
              />
            </Field>
          )}
        </div>

        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-1 size-5 accent-[var(--wm-brand-deep)]"
            checked={draft.consumable ?? false}
            onChange={(e) => setDraft({ ...draft, consumable: e.target.checked })}
          />
          <span className="text-sm">
            Consumable
            <span className="block text-xs text-graphite-2">
              Screws, lumber, caulk. Written off when it leaves the shop instead of being chased up.
            </span>
          </span>
        </label>

        {!isNew && (
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              className="size-5 accent-[var(--wm-brand-deep)]"
              checked={draft.active ?? true}
              onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
            />
            <span className="text-sm">Active</span>
          </label>
        )}

        <ErrorNote>{error}</ErrorNote>

        {/* Two taps, not a browser confirm(): dialogs freeze installed PWAs on
            some phones, and the second tap says exactly what will happen. */}
        {!isNew && (
          <div className="border-t border-line pt-4">
            {confirmDelete ? (
              <div className="space-y-2">
                <p className="text-sm">
                  Delete {draft.name}? It disappears from every list. Past movements keep its name.
                </p>
                <div className="flex gap-2">
                  <Button className="flex-1" onClick={() => setConfirmDelete(false)}>Keep it</Button>
                  <Button variant="danger" className="flex-1" loading={busy} onClick={() => void remove()}>
                    Delete for good
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="ghost" className="w-full text-alarm" onClick={() => setConfirmDelete(true)}>
                Delete this gear
              </Button>
            )}
          </div>
        )}
      </div>
    </Sheet>
  )
}
